import { ReactElement } from 'react';

import { PatientDataAccessAuthorizer } from '@dreryk/egabinet-patient-access-frontend-api';
import { Gender } from '@dreryk/egabinet-patient-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { isAnySpecialModeAvailable, PatientDataAccessMode } from '~libs.shared.prescription';

import { PatientDataAccessAuthorizerField } from './fields/PatientDataAccessAuthorizerField';
import { PatientDataAccessFormAccessModeField } from './fields/PatientDataAccessFormAccessModeField/PatientDataAccessFormAccessModeField';
import { useInitialAccessMode } from './PatientDataAccessForm.hooks';
import { PatientDataAccessFormBean, PatientDataAccessFormController } from './PersonDataAccessForm.types';

type PatientDataAccessFormPropsChildrenRenderProps = {
   handleSubmit: PatientDataAccessFormController['handleSubmit'];
   accessMode: PatientDataAccessMode;
};

type PatientDataAccessFormProps = {
   readonly patientAuthorizers: PatientDataAccessAuthorizer[];
   readonly patientGender: Gender;
   readonly patientAge: number;
   readonly children: (renderProps: PatientDataAccessFormPropsChildrenRenderProps) => ReactElement;
};

export const PatientDataAccessForm = (props: PatientDataAccessFormProps): JSX.Element => {
   const { register, watch, handleSubmit } = useFormController<PatientDataAccessFormBean>({
      authorizer: undefined,
      accessMode: useInitialAccessMode(props.patientGender, props.patientAge),
   });

   const currentAccessMode = watch('accessMode');

   return (
      <StyledFlex direction={'column'} gap={'8px'}>
         {isAnySpecialModeAvailable(props.patientGender, props.patientAge) ? (
            <PatientDataAccessFormAccessModeField
               register={register}
               patientGender={props.patientGender}
               patientAge={props.patientAge}
            />
         ) : null}
         <StyledFlex gap={'8px'} flexWrap={'wrap'} alignItems={'flex-end'}>
            {currentAccessMode === 'STANDARD' ? (
               <PatientDataAccessAuthorizerField register={register} patientAuthorizers={props.patientAuthorizers} />
            ) : null}
            {props.children({ handleSubmit: handleSubmit, accessMode: currentAccessMode })}
         </StyledFlex>
      </StyledFlex>
   );
};
